// DeleteTeamConfirm — confirm-then-archive UI for the manual-team delete
// action (PRODOPSAI-540). Soft-archive only: existing memberships are
// preserved (the ON DELETE CASCADE on team_memberships.team_id would wipe
// history if we issued a real DELETE).

import { useEffect, useState } from 'react';
import type { ITeam } from '@skystone/shared';
import {
  Button,
  Dialog,
  DialogFooter,
  ErrorBanner,
} from '@/components/ui';
import { getErrorMessage } from '@/lib/utils';
import { teamApi } from '@/services/team-api';

interface DeleteTeamConfirmProps {
  open: boolean;
  team: ITeam | null;
  onClose: () => void;
  onConfirmed: (archived: ITeam) => void;
}

export function DeleteTeamConfirm({
  open,
  team,
  onClose,
  onConfirmed,
}: Readonly<DeleteTeamConfirmProps>) {
  const [archiving, setArchiving] = useState<boolean>(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  useEffect(() => {
    if (open === true) return;
    setArchiving(false);
    setArchiveError(null);
  }, [open]);

  const handleConfirm = async () => {
    if (team === null) return;
    setArchiving(true);
    setArchiveError(null);
    try {
      const archived = await teamApi.delete(team.id);
      onConfirmed(archived);
      onClose();
    } catch (err: unknown) {
      setArchiveError(getErrorMessage(err, 'Failed to archive team'));
    } finally {
      setArchiving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Archive team?" maxWidth="max-w-md">
      <div className="space-y-4">
        <p className="text-sm text-foreground" data-testid="delete-team-message">
          Archives <strong>{team?.name ?? ''}</strong>. Existing memberships are preserved
          but the team is hidden from default lists. The Fabric sync will not re-activate
          it. You can restore it any time by ticking <em>Show archived</em> and clicking
          {' '}<em>Re-activate</em>.
        </p>

        {archiveError !== null && <ErrorBanner message={archiveError} />}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={archiving}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            isLoading={archiving}
            onClick={() => void handleConfirm()}
            data-testid="delete-team-confirm"
          >
            Archive team
          </Button>
        </DialogFooter>
      </div>
    </Dialog>
  );
}
